import { useEffect, useMemo, useRef, useState } from "react";
import { getTurnMetrics } from "V2.0/services/conversationService";

const EMPTY = { enabled: false, channel: null, turns: [], summary: null };

// Fetches per-turn metrics only when the conversation's assistant has the staff-only flag.
// refreshKey: anything that should trigger a refetch (we pass the message count).
export default function useTurnMetrics({
  workspaceId,
  conversationId,
  assistantEnabled,
  refreshKey,
}) {
  const [data, setData] = useState(EMPTY);
  // Tracks the last conversation this hook fetched for, so we can tell "the user switched
  // conversations" (clear stale data) apart from "the same conversation just got a new message"
  // (refreshKey bump — keep rendering the last-known data while the refetch is in flight, so the
  // lines don't blink out on every message).
  const lastKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const key = `${workspaceId}:${conversationId}`;
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      setData(EMPTY);
    }

    if (!assistantEnabled || !workspaceId || !conversationId) {
      setData(EMPTY);
      return undefined;
    }
    getTurnMetrics({ workspaceId, conversationId })
      .then((response) => {
        if (!cancelled && response.status === 200) {
          setData({ ...EMPTY, ...response.data });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to load turn metrics", error);
          setData(EMPTY);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, conversationId, assistantEnabled, refreshKey]);

  // Every view joins by message index; the voice/chat difference is resolved server-side.
  const byMessageIndex = useMemo(
    () =>
      Object.fromEntries(
        (data.turns ?? [])
          .filter((t) => t.messageIndex !== null && t.messageIndex !== undefined)
          .map((t) => [t.messageIndex, t]),
      ),
    [data.turns],
  );

  return { ...data, byMessageIndex };
}
